import { Box, Divider, Drawer } from "@mui/material";
import type { GridListViewColDef, GridRowClassNameParams, GridRowParams } from "@mui/x-data-grid-premium";
import { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router";
import DataGrid from "@entrilia/shared/components/grid/DataGrid";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import { logError } from "@entrilia/shared/logging";
import useInboxStateContext from "../../../../contexts/useInboxStateContext";
import type { PreviewDocument } from "../../../../hooks/pdfPreview";
import { usePdfPreview } from "../../../../hooks/pdfPreview";
import { messagesSelector } from "../../../../store/state/messages/selectors";
import type { Message } from "../../../../store/state/messages/types";
import { MessageType } from "../../../../store/state/messages/types";
import { impersonationSelector } from "../../../../store/state/user/selectors";
import InvestorPortalPreviewFileDialog from "../../../common/InvestorPortalPreviewFileDialog";
import Actions from "../Actions";
import NoEmailsComponent from "../noEmails/NoEmailsComponent";
import MessageListViewCell from "./MessageListViewCell";

const getRowClassName = (params: GridRowClassNameParams<Message>) => (params.row.isImportant ? "important" : "");

const listViewColumn: GridListViewColDef<Message> = {
  field: "listColumn",
  renderCell: (params) => <MessageListViewCell message={params.row} id={params.id} />,
};

// MUI DataGrid's built-in dataSource/lazyLoading API requires a server-side data source
// abstraction and does not integrate with our Redux-based criteria/pagination model.
// We use a scroll listener on the virtual scroller instead to stay within the existing
// Redux flow: scrolling near the bottom increments the page in criteria, which triggers
// the existing Redux thunk that fetches and appends the next page.
const MessageListView = () => {
  const { tag } = useParams();

  const [previewFileData, setPreviewFileData] = useState<PreviewDocument | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(false);
  const lastLoadedPageRef = useRef<number>(-1);
  const gridContainerRef = useRef<HTMLDivElement>(null);

  const { sendNotificationError } = useNotificationContext();
  const { prepareMessageForPreview } = usePdfPreview();
  const isImpersonation = useSelector(impersonationSelector);
  const messages = useSelector(messagesSelector);
  const { ui, criteria, setMessageId, updateCriteriaPreload, selection, setSelection, watermarkInvestorName } =
    useInboxStateContext();

  useEffect(() => {
    lastLoadedPageRef.current = -1;
  }, [tag, criteria.query]);

  useEffect(() => {
    const container = gridContainerRef.current;
    if (!container) {
      return;
    }

    const scroller = container.querySelector<HTMLElement>(".MuiDataGrid-virtualScroller");
    if (!scroller) {
      logError(
        new Error(".MuiDataGrid-virtualScroller not found — infinite scroll will not work"),
        "MessageListViewComponent"
      );
      return;
    }

    const handleScroll = () => {
      if (ui.messagesLoading) {
        return;
      }

      if (ui.total === undefined || messages.length >= ui.total) {
        return;
      }

      if (lastLoadedPageRef.current === criteria.page) {
        return;
      }

      const { scrollTop, clientHeight, scrollHeight } = scroller;
      if (scrollTop + clientHeight >= scrollHeight - 200) {
        lastLoadedPageRef.current = criteria.page;
        updateCriteriaPreload({ page: criteria.page + 1, preload: true });
      }
    };

    scroller.addEventListener("scroll", handleScroll);
    return () => scroller.removeEventListener("scroll", handleScroll);
  }, [criteria, messages.length, ui.messagesLoading, ui.total, updateCriteriaPreload]);

  const handleNavigateToMessage = useCallback(
    (params: GridRowParams<Message>) => {
      if (isLoading) {
        return;
      }

      if (params.row.type === MessageType.Letter) {
        setMessageId(params.row.id);
        return;
      }

      setIsLoading(true);
      prepareMessageForPreview(params.row, !isImpersonation, watermarkInvestorName)
        .then((downloadData) => {
          setPreviewFileData(downloadData);
        })
        .catch(() => {
          sendNotificationError("The file cannot be previewed");
        })
        .finally(() => {
          setIsLoading(false);
        });
    },
    [isLoading, prepareMessageForPreview, isImpersonation, watermarkInvestorName, setMessageId, sendNotificationError]
  );

  if (messages.length === 0 && !ui.messagesLoading) {
    return <NoEmailsComponent />;
  }

  const isDrawerOpen = selection.ids.size > 0;

  return (
    <Box ref={gridContainerRef} sx={{ width: "100%", display: "flex", flexDirection: "column", flex: 1 }}>
      <Divider flexItem />
      <DataGrid
        sx={(t) => ({
          mb: isDrawerOpen ? 9 : 1,
          border: "none",
          ".MuiDataGrid-row": {
            borderLeft: "4px solid white",
            "&.important": {
              borderLeftColor: t.palette.warning.light,
            },
            "&:hover": {
              cursor: "pointer",
            },
            ".MuiDataGrid-cell": {
              pl: 0,
              pr: 1,
              borderColor: t.palette.divider,
              outline: "none",
              lineHeight: "inherit",
            },
          },
        })}
        rows={messages}
        columns={[]}
        listView
        listViewColumn={listViewColumn}
        loading={ui.messagesLoading || isLoading}
        onRowSelectionModelChange={setSelection}
        rowSelectionModel={selection}
        rowHeight={100}
        getRowClassName={getRowClassName}
        onRowClick={handleNavigateToMessage}
      />
      {previewFileData && (
        <InvestorPortalPreviewFileDialog
          onClose={() => setPreviewFileData(undefined)}
          url={previewFileData.fileInfo.downloadUrl}
          fileName={previewFileData.fileInfo.fileName}
          documentViewSessionId={previewFileData.fileInfo.documentViewSessionId}
          refreshUrl={previewFileData.refresh}
          preventDownload={previewFileData.preventDownload}
        />
      )}
      <Drawer
        open={isDrawerOpen}
        anchor="bottom"
        sx={{ height: "70px" }}
        hideBackdrop
        variant="temporary"
        slotProps={{
          paper: {
            sx: {
              height: "inherit",
              borderTopLeftRadius: "8px",
              borderTopRightRadius: "8px",
            },
          },
        }}
      >
        <Box sx={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "center", flex: 1 }}>
          <Actions setSelection={setSelection} selection={selection} compact></Actions>
        </Box>
      </Drawer>
    </Box>
  );
};

export default MessageListView;
